/**
 * @license Codistica
 *
 * Copyright (c) 2020, Codistica and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE.md file in the root directory of this source tree.
 *
 * BUILD DATE: Fri Jul 10 2020 16:51:08 GMT+0200 (Central European Summer Time)
 */
import _extends from "@babel/runtime-corejs3/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime-corejs3/helpers/esm/objectWithoutProperties";
import _typeof from "@babel/runtime-corejs3/helpers/esm/typeof";
import _newArrowCheck from "@babel/runtime-corejs3/helpers/esm/newArrowCheck";
import _Array$from from "@babel/runtime-corejs3/core-js-stable/array/from";
import _someInstanceProperty from "@babel/runtime-corejs3/core-js-stable/instance/some";
import _bindInstanceProperty from "@babel/runtime-corejs3/core-js-stable/instance/bind";
import _classCallCheck from "@babel/runtime-corejs3/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime-corejs3/helpers/esm/createClass";
import _assertThisInitialized from "@babel/runtime-corejs3/helpers/esm/assertThisInitialized";
import _inherits from "@babel/runtime-corejs3/helpers/esm/inherits";
import _createSuper from "@babel/runtime-corejs3/helpers/esm/createSuper";
import _defineProperty from "@babel/runtime-corejs3/helpers/esm/defineProperty";

/** @module react/hocs/with-on-drag */
import { eventListenerObjectSupport } from '@codistica/browser';
import { log } from '@codistica/core';
import React from 'react';
import { mergeStyles } from '../modules/merge-styles.js'; // TODO: ADD DAMPING OPTION (FUNCTION)
// TODO: ADD MOMENTUM OPTION (COEFFICIENT)
// TODO: ADD THRESHOLD TO GRAB OPTION (TIME)

var eventOptions = eventListenerObjectSupport.capture ? {
  capture: true
} : true;

/**
 * @typedef withOnDragPropsType
 * @property {boolean} [isolate=false] - Isolate component dragging events. Do not allow propagation to other elements.
 * @property {Function} [onDragStart=null] - Callback for dragStart event.
 * @property {Function} [onDrag=null] - Callback for drag event.
 * @property {Function} [onDragEnd=null] - Callback for dragEnd event.
 * @property {*} [children=null] - React prop.
 * @property {*} [forwardedRef=null] - React prop.
 * @property {Object<string,*>} [style={}] - React prop.
 */

/**
 * @description Creates a higher order component with dragging capabilities.
 * @param {(Object<string,*>|string)} Component - React component.
 * @returns {Object<string,*>} Created higher order component.
 */
function withOnDrag(Component) {
  var _this3 = this,
      _context7;

  /**
   * @classdesc Higher order component.
   */
  var HOC = /*#__PURE__*/function (_React$Component) {
    _inherits(HOC, _React$Component);

    var _super = _createSuper(HOC);

    /**
     * @description Constructor.
     * @param {withOnDragPropsType} [props] - Component props.
     */
    function HOC(props) {
      var _context, _context2, _context3, _context4;

      var _this;

      _classCallCheck(this, HOC);

      _this = _super.call(this, props);

      _defineProperty(_assertThisInitialized(_this), "componentRef", void 0);

      _defineProperty(_assertThisInitialized(_this), "touchIdentifier", void 0);

      _defineProperty(_assertThisInitialized(_this), "xPos", void 0);

      _defineProperty(_assertThisInitialized(_this), "yPos", void 0);

      _this.componentRef = null;
      _this.touchIdentifier = null;
      _this.xPos = 0;
      _this.yPos = 0;
      _this.state = {
        isDragging: false,
        isGrabbed: false
      }; // BIND METHODS

      _assertThisInitialized(_this).onStart = _bindInstanceProperty(_context = _this.onStart).call(_context, _assertThisInitialized(_this));
      _assertThisInitialized(_this).onEnd = _bindInstanceProperty(_context2 = _this.onEnd).call(_context2, _assertThisInitialized(_this));
      _assertThisInitialized(_this).onMove = _bindInstanceProperty(_context3 = _this.onMove).call(_context3, _assertThisInitialized(_this));
      _assertThisInitialized(_this).setComponentRef = _bindInstanceProperty(_context4 = _this.setComponentRef).call(_context4, _assertThisInitialized(_this));
      return _this;
    }
    /**
     * @instance
     * @description React lifecycle.
     * @returns {void} Void.
     */


    _createClass(HOC, [{
      key: "componentDidMount",
      value: function componentDidMount() {
        this.componentRef.addEventListener('touchstart', this.onStart, eventOptions);
        this.componentRef.addEventListener('mousedown', this.onStart, eventOptions);
        this.componentRef.addEventListener('touchend', this.onEnd, eventOptions);
        window.addEventListener('mouseup', this.onEnd, eventOptions);
      }
      /**
       * @instance
       * @description React lifecycle.
       * @returns {void} Void.
       */

    }, {
      key: "componentWillUnmount",
      value: function componentWillUnmount() {
        this.componentRef.removeEventListener('touchstart', this.onStart, eventOptions);
        this.componentRef.removeEventListener('mousedown', this.onStart, eventOptions);
        this.componentRef.removeEventListener('touchend', this.onEnd, eventOptions);
        window.removeEventListener('mouseup', this.onEnd, eventOptions);
      }
      /**
       * @instance
       * @description Callback for start event.
       * @param {Object<string,*>} e - Triggering event.
       * @returns {void} Void.
       */

    }, {
      key: "onStart",
      value: function onStart(e) {
        if (e.type === 'mousedown' && e.button === 0 || e.type === 'touchstart' && e.touches.length === 1) {
          log.debug('onStart()', 'DRAG STARTED')();
          this.componentRef.addEventListener('touchmove', this.onMove, eventOptions);
          window.addEventListener('mousemove', this.onMove, eventOptions);
          this.setState({
            isGrabbed: true
          });

          if (e.type === 'mousedown') {
            this.xPos = e.clientX;
            this.yPos = e.clientY;
          } else {
            this.touchIdentifier = e.touches[0].identifier;
            this.xPos = e.touches[0].screenX;
            this.yPos = e.touches[0].screenY;
          }

          if (typeof this.props.onDragStart === 'function') {
            this.props.onDragStart();
          } // TODO: REMOVE. USE PERTINENT HOCS.


          if (this.props.isolate && e.type === 'touchstart') {
            e.stopPropagation();
            e.cancelable && e.preventDefault();
          }
        }
      }
      /**
       * @instance
       * @description Callback for end event.
       * @param {Object<string,*>} e - Triggering event.
       * @returns {void} Void.
       */

    }, {
      key: "onEnd",
      value: function onEnd(e) {
        var _context5,
            _this2 = this,
            _context6;

        if (this.state.isGrabbed && (e.type === 'mouseup' && e.button === 0 || e.type === 'touchend' && _someInstanceProperty(_context5 = _Array$from(e.changedTouches)).call(_context5, _bindInstanceProperty(_context6 = function _context6(elem) {
          _newArrowCheck(this, _this2);

          return elem.identifier === this.touchIdentifier;
        }).call(_context6, this)))) {
          log.debug('onEnd()', 'DRAG ENDED')();
          this.componentRef.removeEventListener('touchmove', this.onMove, eventOptions);
          window.removeEventListener('mousemove', this.onMove, eventOptions);
          this.setState({
            isDragging: false,
            isGrabbed: false
          });

          if (e.type === 'touchend') {
            this.touchIdentifier = null;
          }

          if (typeof this.props.onDragEnd === 'function') {
            this.props.onDragEnd();
          }
        }
      }
      /**
       * @instance
       * @description Callback for move event.
       * @param {Object<string,*>} e - Triggering event.
       * @returns {void} Void.
       */

    }, {
      key: "onMove",
      value: function onMove(e) {
        if (this.state.isGrabbed && (e.type === 'mousemove' || e.touches.length === 1 || this.state.isDragging)) {
          this.setState({
            isDragging: true
          });

          if (typeof this.props.onDrag === 'function') {
            if (e.type === 'mousemove') {
              this.props.onDrag({
                deltaX: e.clientX - this.xPos,
                deltaY: e.clientY - this.yPos
              });
              this.xPos = e.clientX;
              this.yPos = e.clientY;
            } else {
              this.props.onDrag({
                deltaX: e.touches[0].screenX - this.xPos,
                deltaY: e.touches[0].screenY - this.yPos
              });
              this.xPos = e.touches[0].screenX;
              this.yPos = e.touches[0].screenY;
            }
          }
        }
      }
      /**
       * @instance
       * @description Save and pass component reference.
       * @param {Object<string,*>} ref - Component reference.
       * @returns {void} Void.
       */

    }, {
      key: "setComponentRef",
      value: function setComponentRef(ref) {
        // FORWARD REF
        var forwardedRef = this.props.forwardedRef;

        if (typeof forwardedRef === 'function') {
          forwardedRef(ref);
        } else if (_typeof(forwardedRef) === 'object' && forwardedRef !== null && typeof forwardedRef.current !== 'undefined') {
          forwardedRef.current = ref;
        } // SAVE REF


        this.componentRef = ref;
      }
      /**
       * @instance
       * @description React render method.
       * @returns {Object<string,*>} React component.
       */

    }, {
      key: "render",
      value: function render() {
        var _this$props = this.props,
            onDrag = _this$props.onDrag,
            onDragStart = _this$props.onDragStart,
            onDragEnd = _this$props.onDragEnd,
            children = _this$props.children,
            forwardedRef = _this$props.forwardedRef,
            isolate = _this$props.isolate,
            style = _this$props.style,
            other = _objectWithoutProperties(_this$props, ["onDrag", "onDragStart", "onDragEnd", "children", "forwardedRef", "isolate", "style"]);

        var isGrabbed = this.state.isGrabbed;
        return /*#__PURE__*/React.createElement(Component, _extends({}, other, {
          ref: this.setComponentRef,
          style: mergeStyles(style, {
            cursor: isGrabbed ? 'grabbing' : 'grab'
          })
        }), children);
      }
    }]);

    return HOC;
  }(React.Component);

  _defineProperty(HOC, "defaultProps", {
    isolate: false,
    onDragStart: null,
    onDrag: null,
    onDragEnd: null,
    children: null,
    forwardedRef: null,
    style: {}
  });

  return React.forwardRef(_bindInstanceProperty(_context7 = function _context7(props, ref) {
    _newArrowCheck(this, _this3);

    return /*#__PURE__*/React.createElement(HOC, _extends({}, props, {
      forwardedRef: ref
    }));
  }).call(_context7, this));
}

export { withOnDrag };